import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MessageSquare, Plus, Search, Send, Smile, UserPlus, Users, X, LogOut } from "lucide-react";
import AuthContext from "../context/AuthContext";
import chatService from "../services/chatService";
import { useChatRealtime, useChatRealtimeStatus } from "../hooks/useChatRealtime";
import EmojiKeyboard from "../components/EmojiKeyboard";
import useCustomToast from "../hooks/useCustomToast";
import { useTranslation } from "../i18n";

const POLL_THREAD_MS = 3000;
const POLL_LIST_MS = 30000;

const initials = (name) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

const Avatar = ({ user, size = "w-9 h-9", text = "text-sm" }) =>
  user?.avatar_url ? (
    <img src={user.avatar_url} alt="" className={`${size} rounded-full object-cover flex-shrink-0`} />
  ) : (
    <div
      className={`${size} rounded-full org-bg-primary text-white flex items-center justify-center font-semibold flex-shrink-0 ${text}`}
    >
      {initials(user?.full_name || user?.email)}
    </div>
  );

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" });

const titleOf = (conv, t) => {
  if (!conv) return "";
  if (conv.kind === "direct") return conv.counterpart?.full_name || conv.counterpart?.email || t("thread.unknown");
  return conv.name || conv.members.map((m) => (m.full_name || m.email || "").split(" ")[0]).join(", ");
};

const ChatPage = () => {
  const { t } = useTranslation("chat");
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { user: me } = useContext(AuthContext);
  const realtime = useChatRealtimeStatus();
  const live = realtime === "open";

  const [showNew, setShowNew] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [typingBy, setTypingBy] = useState(null);
  const typingTimer = useRef(null);

  // ---- conversations ---------------------------------------------------------
  const { data: conversations = [], isLoading: loadingList } = useQuery({
    queryKey: ["chat", "conversations"],
    queryFn: chatService.listConversations,
    refetchInterval: live ? false : POLL_LIST_MS,
  });
  const active = useMemo(
    () => conversations.find((c) => c.id === conversationId) || null,
    [conversations, conversationId]
  );

  // ---- messages --------------------------------------------------------------
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const latestAt = useRef(null);
  const bottomRef = useRef(null);
  const listRef = useRef(null);

  const appendMessages = useCallback((incoming) => {
    if (!incoming.length) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => !seen.has(m.id));
      if (!fresh.length) return prev;
      const next = [...prev, ...fresh].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      latestAt.current = next[next.length - 1].created_at;
      return next;
    });
  }, []);

  useEffect(() => {
    setMessages([]);
    setHasMore(false);
    latestAt.current = null;
    setTypingBy(null);
    setShowMembers(false);
    if (!conversationId) return undefined;
    let cancelled = false;
    chatService
      .listMessages(conversationId, { limit: 50 })
      .then((rows) => {
        if (cancelled) return;
        setMessages(rows);
        setHasMore(rows.length === 50);
        latestAt.current = rows.length ? rows[rows.length - 1].created_at : null;
        chatService.markRead(conversationId).then(() => {
          queryClient.invalidateQueries({ queryKey: ["chat", "unread"] });
          queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
        });
      })
      .catch(() => {
        if (!cancelled) {
          toast.error(t("errors.not_found"));
          navigate("/chat", { replace: true });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Polling fallback for the open thread when the socket is not live.
  useEffect(() => {
    if (!conversationId || live) return undefined;
    const id = setInterval(() => {
      if (!latestAt.current) return;
      chatService
        .listMessages(conversationId, { after: latestAt.current, limit: 100 })
        .then(appendMessages)
        .catch(() => {});
    }, POLL_THREAD_MS);
    return () => clearInterval(id);
  }, [conversationId, live, appendMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, conversationId]);

  // ---- realtime ----------------------------------------------------------------
  const onEvent = useCallback(
    (ev) => {
      if (ev.type === "message") {
        if (ev.conversation_id === conversationId) {
          appendMessages([ev.message]);
          if (ev.message?.sender?.id !== me?.id) {
            chatService.markRead(conversationId).catch(() => {});
          }
          setTypingBy(null);
        } else {
          queryClient.invalidateQueries({ queryKey: ["chat", "unread"] });
        }
        queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
      } else if (ev.type === "typing") {
        if (ev.conversation_id === conversationId && ev.user_id !== me?.id) {
          setTypingBy(ev.full_name || t("thread.someone"));
          if (typingTimer.current) clearTimeout(typingTimer.current);
          typingTimer.current = setTimeout(() => setTypingBy(null), 3000);
        }
      } else if (ev.type === "conversation.created" || ev.type === "conversation.updated") {
        queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
      }
    },
    [conversationId, me?.id, appendMessages, queryClient, t]
  );
  useChatRealtime(onEvent);

  // ---- composer ----------------------------------------------------------------
  const [draft, setDraft] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const emojiBtnRef = useRef(null);
  const textareaRef = useRef(null);
  const lastTypingSent = useRef(0);

  const sendMutation = useMutation({
    mutationFn: ({ id, body }) => chatService.sendMessage(id, body),
    onSuccess: (msg) => {
      appendMessages([msg]);
      queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
    onError: (e) => toast.error(e.message),
  });

  const send = () => {
    const body = draft.trim();
    if (!body || !conversationId || sendMutation.isPending) return;
    setDraft("");
    sendMutation.mutate({ id: conversationId, body });
  };

  const onDraftChange = (value) => {
    setDraft(value);
    if (!live || !conversationId) return;
    const now = Date.now();
    if (now - lastTypingSent.current > 2000) {
      lastTypingSent.current = now;
      chatService.typing(conversationId).catch(() => {});
    }
  };

  const insertEmoji = (native) => {
    const el = textareaRef.current;
    if (!el) {
      setDraft((d) => d + native);
      return;
    }
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + native + draft.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + native.length, start + native.length);
    });
  };

  const loadEarlier = () => {
    if (!messages.length) return;
    chatService
      .listMessages(conversationId, { before: messages[0].created_at, limit: 50 })
      .then((rows) => {
        setHasMore(rows.length === 50);
        setMessages((prev) => [...rows, ...prev]);
      })
      .catch(() => {});
  };

  // ---- members -----------------------------------------------------------------
  const leaveMutation = useMutation({
    mutationFn: ({ id, userId }) => chatService.removeMember(id, userId),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
      if (vars.userId === me?.id) navigate("/chat");
    },
    onError: (e) => toast.error(e.message),
  });

  const grouped = useMemo(() => {
    const out = [];
    let day = null;
    for (const m of messages) {
      const d = new Date(m.created_at).toDateString();
      if (d !== day) {
        day = d;
        out.push({ divider: dayFmt.format(new Date(m.created_at)), key: `d-${d}` });
      }
      out.push(m);
    }
    return out;
  }, [messages]);

  const showList = !conversationId;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="bg-white rounded-2xl shadow-md overflow-hidden flex" style={{ height: "calc(100vh - 8rem)" }}>
        {/* Conversation list */}
        <aside
          className={`${showList ? "flex" : "hidden"} md:flex w-full md:w-80 lg:w-96 flex-col border-r border-slate-200`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              {t("title")}
            </h1>
            <button
              onClick={() => setShowNew(true)}
              className="p-2 rounded-full org-bg-primary org-btn text-white"
              aria-label={t("new.title")}
              title={t("new.title")}
            >
              <Plus size={18} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingList ? (
              <p className="p-4 text-sm text-slate-500">{t("list.loading")}</p>
            ) : conversations.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">{t("list.empty")}</p>
                <button onClick={() => setShowNew(true)} className="mt-3 text-sm font-medium text-purple-600 hover:underline">
                  {t("list.start")}
                </button>
              </div>
            ) : (
              conversations.map((c) => {
                const isActive = c.id === conversationId;
                const other = c.kind === "direct" ? c.counterpart : null;
                return (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/chat/${c.id}`)}
                    className={`w-full text-left flex items-center gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${
                      isActive ? "bg-purple-50" : ""
                    }`}
                  >
                    {other ? (
                      <Avatar user={other} />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center flex-shrink-0">
                        <Users size={16} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`truncate text-sm ${c.unread_count ? "font-semibold text-slate-900" : "text-slate-800"}`}>
                          {titleOf(c, t)}
                        </span>
                        {c.last_message_at && (
                          <span className="text-[11px] text-slate-400 flex-shrink-0">
                            {timeFmt.format(new Date(c.last_message_at))}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-xs text-slate-500">
                          {c.last_message
                            ? `${c.last_message.sender.id === me?.id ? t("list.you") : (c.last_message.sender.full_name || "").split(" ")[0]}: ${c.last_message.body}`
                            : t("list.no_messages")}
                        </span>
                        {c.unread_count > 0 && (
                          <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full org-bg-primary text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
                            {c.unread_count > 99 ? "99+" : c.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {realtime === "disabled" && (
            <p className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-100">{t("status.polling")}</p>
          )}
        </aside>

        {/* Thread */}
        <section className={`${showList ? "hidden" : "flex"} md:flex flex-1 flex-col min-w-0`}>
          {!conversationId ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">{t("thread.pick")}</div>
          ) : (
            <>
              <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
                <button onClick={() => navigate("/chat")} className="md:hidden p-1 text-slate-500" aria-label={t("common:actions.back")}>
                  <ArrowLeft size={20} />
                </button>
                {active?.kind === "direct" ? (
                  <Avatar user={active.counterpart} />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center">
                    <Users size={16} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold text-slate-900 truncate">{titleOf(active, t)}</h2>
                  <p className="text-xs text-slate-500 truncate">
                    {typingBy
                      ? t("thread.typing", { name: typingBy })
                      : active?.kind === "group"
                        ? t("thread.members", { count: active.members.length })
                        : active?.counterpart?.email || ""}
                  </p>
                </div>
                {active?.kind === "group" && (
                  <button
                    onClick={() => setShowMembers((v) => !v)}
                    className="p-2 rounded-full hover:bg-slate-100 text-slate-600"
                    title={t("members.title")}
                    aria-label={t("members.title")}
                  >
                    <Users size={18} />
                  </button>
                )}
              </header>

              <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-1 bg-slate-50/50">
                {hasMore && (
                  <div className="text-center">
                    <button onClick={loadEarlier} className="text-xs text-purple-600 hover:underline">
                      {t("thread.load_earlier")}
                    </button>
                  </div>
                )}
                {grouped.map((m) =>
                  m.divider ? (
                    <div key={m.key} className="text-center text-[11px] text-slate-400 py-2">
                      {m.divider}
                    </div>
                  ) : (
                    <MessageBubble key={m.id} message={m} mine={m.sender.id === me?.id} showSender={active?.kind === "group"} />
                  )
                )}
                <div ref={bottomRef} />
              </div>

              <footer className="border-t border-slate-200 p-3">
                <div className="flex items-end gap-2">
                  <div className="relative">
                    <button
                      ref={emojiBtnRef}
                      type="button"
                      onClick={() => setEmojiOpen((v) => !v)}
                      className={`p-2 rounded-full hover:bg-slate-100 ${emojiOpen ? "text-purple-600" : "text-slate-500"}`}
                      aria-label={t("composer.emoji")}
                    >
                      <Smile size={20} />
                    </button>
                    {emojiOpen && (
                      <EmojiKeyboard anchorRef={emojiBtnRef} onSelect={insertEmoji} onClose={() => setEmojiOpen(false)} />
                    )}
                  </div>
                  <textarea
                    ref={textareaRef}
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    rows={1}
                    maxLength={4000}
                    placeholder={t("composer.placeholder")}
                    className="flex-1 resize-none max-h-40 px-3 py-2 border border-slate-300 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={send}
                    disabled={!draft.trim() || sendMutation.isPending}
                    className="p-2.5 rounded-full org-bg-primary org-btn text-white disabled:opacity-40"
                    aria-label={t("composer.send")}
                  >
                    <Send size={18} />
                  </button>
                </div>
              </footer>
            </>
          )}
        </section>

        {/* Members panel (groups) */}
        {showMembers && active?.kind === "group" && (
          <MembersPanel
            conversation={active}
            me={me}
            onClose={() => setShowMembers(false)}
            onLeave={() => leaveMutation.mutate({ id: active.id, userId: me.id })}
            onRemove={(userId) => leaveMutation.mutate({ id: active.id, userId })}
          />
        )}
      </div>

      {showNew && (
        <NewConversationModal
          onClose={() => setShowNew(false)}
          onOpened={(conv) => {
            setShowNew(false);
            queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
            navigate(`/chat/${conv.id}`);
          }}
        />
      )}
    </div>
  );
};

const MessageBubble = ({ message, mine, showSender }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
      {showSender && !mine && (
        <span className="text-[11px] text-slate-500 ml-1 mb-0.5">{message.sender.full_name || message.sender.email}</span>
      )}
      <div
        className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
          mine ? "org-bg-primary text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"
        }`}
      >
        {message.body}
      </div>
      <span className="text-[10px] text-slate-400 mt-0.5 mx-1">{timeFmt.format(new Date(message.created_at))}</span>
    </div>
  </div>
);

const useColleagueSearch = () => {
  const [q, setQ] = useState("");
  const { data, isFetching } = useQuery({
    queryKey: ["chat", "colleagues", q],
    queryFn: () => chatService.searchColleagues(q),
    staleTime: 30000,
  });
  return { q, setQ, results: data?.data || [], isFetching };
};

const NewConversationModal = ({ onClose, onOpened }) => {
  const { t } = useTranslation("chat");
  const toast = useCustomToast();
  const [mode, setMode] = useState("direct");
  const [picked, setPicked] = useState([]);
  const [name, setName] = useState("");
  const { q, setQ, results, isFetching } = useColleagueSearch();

  const openDirect = useMutation({
    mutationFn: (userId) => chatService.openDirect(userId),
    onSuccess: onOpened,
    onError: (e) => toast.error(e.message),
  });
  const createGroup = useMutation({
    mutationFn: () => chatService.createGroup({ name: name.trim(), memberIds: picked.map((u) => u.id) }),
    onSuccess: onOpened,
    onError: (e) => toast.error(e.message),
  });

  const toggle = (u) =>
    setPicked((prev) => (prev.some((p) => p.id === u.id) ? prev.filter((p) => p.id !== u.id) : [...prev, u]));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">{t("new.title")}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100 text-slate-500" aria-label={t("common:actions.close")}>
            <X size={20} />
          </button>
        </div>
        <div className="px-4 pt-3 flex gap-2">
          {["direct", "group"].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 rounded-full text-sm ${mode === m ? "org-bg-primary text-white" : "bg-slate-100 text-slate-700"}`}
            >
              {t(`new.${m}`)}
            </button>
          ))}
        </div>
        {mode === "group" && (
          <div className="px-4 pt-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder={t("new.group_name")}
              className="w-full px-3 py-2 border border-slate-300 rounded-full text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {picked.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {picked.map((u) => (
                  <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-xs">
                    {u.full_name || u.email}
                    <button onClick={() => toggle(u)} aria-label={t("common:actions.remove")}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="px-4 pt-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("new.search")}
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-full text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 mt-1">
          {isFetching && results.length === 0 ? (
            <p className="p-3 text-sm text-slate-500">{t("list.loading")}</p>
          ) : results.length === 0 ? (
            <p className="p-3 text-sm text-slate-500">{t("new.no_results")}</p>
          ) : (
            results.map((u) => {
              const selected = picked.some((p) => p.id === u.id);
              return (
                <button
                  key={u.id}
                  onClick={() => (mode === "direct" ? openDirect.mutate(u.id) : toggle(u))}
                  disabled={openDirect.isPending}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left hover:bg-slate-50 ${selected ? "bg-purple-50" : ""}`}
                >
                  <Avatar user={u} size="w-8 h-8" text="text-xs" />
                  <div className="min-w-0">
                    <p className="text-sm text-slate-800 truncate">{u.full_name || u.email}</p>
                    {u.full_name && <p className="text-xs text-slate-500 truncate">{u.email}</p>}
                  </div>
                </button>
              );
            })
          )}
        </div>
        {mode === "group" && (
          <div className="p-4 border-t border-slate-200">
            <button
              onClick={() => createGroup.mutate()}
              disabled={picked.length === 0 || createGroup.isPending}
              className="w-full px-4 py-2 org-bg-primary org-btn text-white rounded-full text-sm disabled:opacity-50"
            >
              {createGroup.isPending ? t("new.creating") : t("new.create_group", { count: picked.length })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const MembersPanel = ({ conversation, me, onClose, onLeave, onRemove }) => {
  const { t } = useTranslation("chat");
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const { q, setQ, results } = useColleagueSearch();
  const isOwner = conversation.my_role === "owner";
  const memberIds = new Set(conversation.members.map((m) => m.id));

  const addMutation = useMutation({
    mutationFn: (userId) => chatService.addMembers(conversation.id, [userId]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
      setAdding(false);
      setQ("");
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <aside className="w-72 border-l border-slate-200 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <h3 className="font-semibold text-slate-900 text-sm">{t("members.title")}</h3>
        <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100 text-slate-500" aria-label={t("common:actions.close")}>
          <X size={18} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {conversation.members.map((m) => (
          <div key={m.id} className="flex items-center gap-2 px-4 py-2">
            <Avatar user={m} size="w-8 h-8" text="text-xs" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-800 truncate">
                {m.full_name || m.email}
                {m.id === me?.id && <span className="text-slate-400"> {t("members.you")}</span>}
              </p>
            </div>
            {isOwner && m.id !== me?.id && (
              <button onClick={() => onRemove(m.id)} className="text-xs text-red-600 hover:underline">
                {t("members.remove")}
              </button>
            )}
          </div>
        ))}
        {adding && (
          <div className="px-4 py-2 border-t border-slate-100">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("new.search")}
              className="w-full px-3 py-1.5 border border-slate-300 rounded-full text-sm mb-2"
            />
            {results
              .filter((u) => !memberIds.has(u.id))
              .map((u) => (
                <button
                  key={u.id}
                  onClick={() => addMutation.mutate(u.id)}
                  disabled={addMutation.isPending}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-slate-50"
                >
                  <Avatar user={u} size="w-7 h-7" text="text-[10px]" />
                  <span className="text-sm text-slate-800 truncate">{u.full_name || u.email}</span>
                </button>
              ))}
          </div>
        )}
      </div>
      <div className="p-3 border-t border-slate-200 space-y-2">
        <button
          onClick={() => setAdding((v) => !v)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-slate-100 text-slate-700 text-sm hover:bg-slate-200"
        >
          <UserPlus size={16} /> {t("members.add")}
        </button>
        <button
          onClick={onLeave}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full text-red-600 text-sm hover:bg-red-50"
        >
          <LogOut size={16} /> {t("members.leave")}
        </button>
      </div>
    </aside>
  );
};

export default ChatPage;
